import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { article as articleData } from "shared/data/article";

import { getArticleType } from "scmp-app/components/article/article-render/helpers";
import { usePlusArticlePopup } from "scmp-app/components/plus/article-popup/hooks";
import type { highlightBlockItemContent$key } from "scmp-app/queries/__generated__/highlightBlockItemContent.graphql";

import {
  StyledContentItemPlusOpinion,
  StyledContentItemPlusPrimary,
  StyledContentItemPlusSecondary,
} from "./styles";

export type Props = {
  reference: highlightBlockItemContent$key;
};

export const HighlightBlockItem: FunctionComponent<Props> = ({ reference }) => {
  const content = useFragment(
    graphql`
      fragment highlightBlockItemContent on Content {
        __typename
        entityId
        ...helpersGetArticleTypeArticle
        ...hooksPlusArticleLinkHandlerArticle
        ...plusOpinionContentItemContent
        ...plusPrimaryContentItemContent
        ...plusSecondaryContentItemContent
        ...helpersCheckArticleIsInSectionsArticle
      }
    `,
    reference,
  );

  const { handleOpen } = usePlusArticlePopup();

  // 'Page' type content is expected to redirect user to a new page
  // ref: handleOpen() -> checkCanShowInPlusPlatform() -> false
  if (content.__typename === "Page") {
    return (
      <StyledContentItemPlusPrimary
        onClick={() =>
          handleOpen(content, { pageType: "homepage", widgetName: "highlight_from_plus" })
        }
        reference={content}
      />
    );
  }

  switch (content.__typename) {
    case "Page":
    case "Podcast":
    case "Video":
      return (
        <StyledContentItemPlusPrimary
          onClick={() =>
            handleOpen(content, { pageType: "homepage", widgetName: "highlight_from_plus" })
          }
          reference={content}
        />
      );
    default:
      break;
  }

  const articleTypeId = getArticleType(content) ?? "";

  switch (articleTypeId) {
    case articleData.types.betweenTheLines.entityId:
    case articleData.types.chinaFutureTechBriefing.entityId:
    case articleData.types.factSheet.entityId:
    case articleData.types.infographic.entityId:
    case articleData.types.series.entityId:
      return (
        <StyledContentItemPlusPrimary
          onClick={() =>
            handleOpen(content, { pageType: "homepage", widgetName: "highlight_from_plus" })
          }
          reference={content}
        />
      );
    case articleData.types.column.entityId:
    case articleData.types.opinion.entityId:
      return (
        <StyledContentItemPlusOpinion
          onClick={() =>
            handleOpen(content, { pageType: "homepage", widgetName: "highlight_from_plus" })
          }
          reference={content}
          withAuthor
        />
      );
    default:
      return (
        <StyledContentItemPlusSecondary
          onClick={() =>
            handleOpen(content, { pageType: "homepage", widgetName: "highlight_from_plus" })
          }
          reference={content}
        />
      );
  }
};

HighlightBlockItem.displayName = "HighlightBlockItem";
